import React, { useContext, useEffect, useState } from 'react';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { Alert, Typography, Link } from '@one-thd/sui-atomic-components';
import { ExperienceContext, useConfigService } from '@thd-nucleus/experience-context';
import {
  NonCardholderView,
  CardholderView,
  AccountSelectorDrawer
} from '@thd-olt-component-react/consumer-card-messaging';
import { Breadcrumbs } from '@thd-olt-component-react/breadcrumbs';
import { QueryProvider, extend } from '@thd-nucleus/data-sources';
import {
  CustomerAccountNavigation,
  useAccountCrumbs,
} from '@thd-olt-component-react/customer-account-navigation';
import { isInStore } from '../utils/user';
import { getAccountSnapshot, getCitiUrl, delink, buildChildRequestObject } from '../api/sso';
import {
  redirectText,
  defaultCitiUrl,
  delinkErrorMessage,
  delinkSuccessMessage,
  payAndManageUrl,
  accountUnlinkMessage
} from '../utils/constants';
import { getUrlParam, redirectToLogin } from '../utils/general';
import { SsoRedirectForm } from '../utils/ssoRedirectForm';
import * as analytics from '../utils/analytics';
import { LoadingScreen } from '../component/loading-screen/LoadingScreen';
import { newRelic, logAccountSnapshot } from '../utils/newRelic';
import { mapCrumbsToStaticCrumbs } from '../utils/breadcrumbs';
import { getSvocId, getUserId } from '../utils/customer';

const ThdCreditCardContent = () => {
  const { channel, isConsumerApp } = useContext(ExperienceContext);
  const isMobile = channel === 'mobile';

  const initialLoader = {
    display: true,
    message: 'Loading Your Information...'
  };

  const crumbs = mapCrumbsToStaticCrumbs(useAccountCrumbs([
    {
      label: 'Home Depot Credit Card',
    },
  ]));

  const [inStore, setInStore] = useState(false);
  const [loader, setLoader] = useState(initialLoader);
  const [isSsoSetup, setIsSsoSetup] = useState(false);
  const [consumerLinked, setConsumerLinked] = useState(false);
  const [crcLinked, setCrcLinked] = useState(false);
  const [proxLinked, setProxLinked] = useState(false);
  const [accounts, setAccounts] = useState(null);
  const [error, setError] = useState(null);
  const [showAccountSelectorDrawer, setShowAccountSelectorDrawer] = useState(false);
  const [showPayAndManageDrawer, setShowPayAndManageDrawer] = useState(false);
  const [ssoFormData, setSsoFormData] = useState(null);
  const [delinkSuccess, setDelinkSuccess] = useState(null);
  const ssoredirect = getUrlParam('ssoredirect');
  const codeParamForSSOLinkAccount = getUrlParam('code');
  const isUserInSsoSetupFlow = Boolean(codeParamForSSOLinkAccount);

  const accountSnapshotResponseHandler = (data) => {
    if (data.accounts && data.accounts.length > 0) {
      logAccountSnapshot(data);
      setIsSsoSetup(data.ssoExist);
      setAccounts(data.accounts);
      setConsumerLinked(data.plcn_sso);
      setCrcLinked(data.plcr_sso);
      setProxLinked(data.plnp_sso);
    } else if (isSsoSetup && !data.ssoExist) {
      setIsSsoSetup(data.ssoExist);
      setAccounts(null);
      setConsumerLinked(data.plcn_sso);
      setCrcLinked(data.plcr_sso);
      setProxLinked(data.plnp_sso);
    } else if (data.errors && data.errors.errors.length > 0) {
      setIsSsoSetup(data.ssoExist);
      setError(data.errors.errors[0]);
      newRelic(data.errors.errors[0].errorCode, data.errors.errors[0].errorMessage);
    }
    setLoader({ display: false, message: '' });
  };

  const getLoaderMessage = () => {
    if (isUserInSsoSetupFlow) {
      return 'Linking your accounts...';
    }
    return 'Redirecting You to CitiBank...';
  };

  const invokeCitiRedirect = (
    citiPageIndicator,
    analyticsTag,
    siteId = 'PLCN_HOMEDEPOT',
    accountId,
    additionalParam
  ) => {
    setLoader({ display: true, message: getLoaderMessage() });
    if (analyticsTag) {
      analytics.postEvent(
        'button submission',
        'my account settings',
        analyticsTag
      );
    }
    getCitiUrl(siteId, additionalParam)
      .then((data) => {
        let citiUrl = '';
        if (data && data.urlType) {
          if (data.urlType === 'AUTHORIZE' || data.urlType === 'DEFAULT') {
            citiUrl = data.url ? data.url : (payAndManageUrl?.[siteId] ?? defaultCitiUrl);
          } else if (data.urlType === 'SSOLOGIN') {
            // eslint-disable-next-line no-param-reassign
            data.processIndicator = typeof citiPageIndicator === 'string' && citiPageIndicator
              ? citiPageIndicator
              : data.processIndicator;
            if (typeof accountId === 'string' && accountId) {
              // eslint-disable-next-line no-param-reassign
              data.accountId = accountId;
            }
            setSsoFormData(data);
            return;
          }
        } else if (data && data.response && data.response.status === 401) {
          redirectToLogin();
          return;
        } else {
          citiUrl = payAndManageUrl?.[siteId] ?? defaultCitiUrl;
        }
        window.location.href = citiUrl;
      });
  };

  useEffect(() => {
    if (!(getSvocId() && getUserId())) {
      redirectToLogin();
    } else if (ssoredirect) {
      const siteId = `${ssoredirect}_HOMEDEPOT`;
      const account = Object.keys(accountUnlinkMessage).find((item) => (item === siteId));
      invokeCitiRedirect('', '', account ? siteId : 'PLCN_HOMEDEPOT');
    } else if (isUserInSsoSetupFlow) {
      // When citi redirects back to this page in the SSO Setup flow
      // it sends in the url the sideId param
      newRelic(null, 'SSO Setup flow', getSvocId());
      const siteIdFromSsoSetupFlow = getUrlParam('siteId');

      invokeCitiRedirect('', '', siteIdFromSsoSetupFlow, undefined, `code=${codeParamForSSOLinkAccount}`);
    } else {
      setInStore(isInStore());
      getAccountSnapshot().then((data, url) => {
        accountSnapshotResponseHandler(data);
      });
    }
  }, []);

  const accountSelectorHandler = (siteId) => {
    if (showPayAndManageDrawer) {
      window.location.href = payAndManageUrl[siteId];
    } else {
      invokeCitiRedirect('', '', siteId);
    }
  };

  const invokeDelink = (accountType) => {
    setLoader({ display: true, message: 'Unlinking Your Accounts...' });
    analytics.postEvent(
      'button submission',
      'my account settings',
      'plcc:unlink accounts'
    );
    delink(accountType)
      .then((isDelinkSuccess) => {
        if (isDelinkSuccess) {
          setDelinkSuccess(delinkSuccessMessage(accountType));
          setError(null);
          getAccountSnapshot()
            .then((data) => {
              accountSnapshotResponseHandler(data);
              window.scrollTo(0, 0);
            });
        } else {
          setError({ errorMessage: delinkErrorMessage });
          setLoader({ display: false, message: '' });
        }
      });
  };

  const updateChildAccount = (account) => {
    return getAccountSnapshot(buildChildRequestObject(account));
  };

  const openLinkAccountsDrawer = () => {
    setShowAccountSelectorDrawer(true);
    setShowPayAndManageDrawer(false);
  };

  const openPayAndManageDrawer = () => {
    setShowAccountSelectorDrawer(true);
    setShowPayAndManageDrawer(true);
  };

  const errorMessageRedirect = () => {
    if (redirectText[error.errorCode] === redirectText.SSO_SM_500) {
      openPayAndManageDrawer();
    } else {
      invokeCitiRedirect();
    }
  };

  const errorAlert = () => {
    if (error && error.errorMessage) {
      const closeText = error.errorCode ? redirectText[error.errorCode] : null;
      const onClose = error.errorCode ? errorMessageRedirect : null;
      return (
        <Alert status="error">
          <div className="sui-flex sui-gap-1 sui-flex-wrap">
            <Typography>{error.errorMessage}</Typography>
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <Link onClick={onClose} target="_blank">
              {closeText}
            </Link>
          </div>
        </Alert>
      );
    }
    return null;
  };

  const renderThdCreditCardBody = () => {
    const showCardholderView = isSsoSetup && accounts && accounts.length > 0;

    return (
      <div>
        {errorAlert()}
        {delinkSuccess
          && <Alert status="success">{delinkSuccess}</Alert>}
        {showAccountSelectorDrawer && (
          <AccountSelectorDrawer
            payAndManage={showPayAndManageDrawer}
            consumerLinked={consumerLinked}
            crcLinked={crcLinked}
            proxLinked={proxLinked}
            closeDrawer={() => setShowAccountSelectorDrawer(false)}
            redirectToCiti={accountSelectorHandler}
            isMobile={isMobile}
          />
        )}
        {!showCardholderView && (
          <NonCardholderView
            disableSSO={inStore || error !== null}
            linkAccount={openLinkAccountsDrawer}
            payAndManage={openPayAndManageDrawer}
            redirectToCiti={invokeCitiRedirect}
            unlinkedAccounts={accounts}
          />
        )}
        {showCardholderView && (
          <CardholderView
            accounts={accounts}
            unlinkAccount={invokeDelink}
            linkAccount={openLinkAccountsDrawer}
            redirectToCiti={invokeCitiRedirect}
            updateAccountsForChildAccounts={updateChildAccount}
          />
        )}
        {ssoFormData && (
          <SsoRedirectForm ssoResponse={ssoFormData} isUserInSsoSetupFlow={isUserInSsoSetupFlow} />
        )}
      </div>
    );
  };

  if (loader.display) {
    return (
      <>
        <LoadingScreen message={loader.message} />
        {ssoFormData && (
          <SsoRedirectForm ssoResponse={ssoFormData} isUserInSsoSetupFlow={isUserInSsoSetupFlow} />
        )}
      </>
    );
  }

  return (
    <div className="grid isBound">
      <div className="col__12-12">
        <div className="sui-grid sui-gap-3 sui-py-3">
          {!isConsumerApp && (
            <ErrorBoundary name="breadcrumbs">
              <Breadcrumbs staticCrumbs={crumbs} />
            </ErrorBoundary>
          )}
          <Typography weight="display" variant="h1" uppercase>
            HOME DEPOT CREDIT CARD
          </Typography>
        </div>

        <div className="sui-grid sui-grid-cols-12 sui-gap-5 sui-py-3">
          <div className="sui-col-span-12 lg:sui-col-span-3">
            <ErrorBoundary name="customer-account-navigation">
              <CustomerAccountNavigation />
            </ErrorBoundary>
          </div>

          <div className="sui-col-span-12 lg:sui-col-span-9">
            <ErrorBoundary name="payments-content">
              {renderThdCreditCardBody()}
            </ErrorBoundary>
          </div>
        </div>
      </div>
    </div>
  );
};

ThdCreditCardContent.dataModel = extend(
  CustomerAccountNavigation,
  Breadcrumbs,
);

ThdCreditCardContent.propTypes = {
};

ThdCreditCardContent.displayName = 'ThdCreditCardContent';

ThdCreditCardContent.defaultProps = {
  name: '',
};

export const ThdCreditCard = () => {
  return (
    <QueryProvider
      defaultVariables={{
        channelId: 1,
      }}
      cacheKey="thd-credit-card"
    >
      <ThdCreditCardContent />
    </QueryProvider>
  );
};

ThdCreditCard.propTypes = {};
ThdCreditCard.displayName = 'ThdCreditCard';
